import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import {
  LayoutDashboard, Users, Building2, CreditCard, Share2, ShieldAlert,
  Plug, BarChart3, Server, Settings as SettingsIcon, LogOut, Menu, X, ArrowLeft,
} from "lucide-react";

const LOGO = "/ashra-logo.png";

const NAV = [
  { id: "overview",       label: "Overview",       icon: LayoutDashboard, testid: "admin-nav-overview" },
  { id: "users",          label: "Users",          icon: Users,           testid: "admin-nav-users" },
  { id: "workspaces",     label: "Workspaces",     icon: Building2,       testid: "admin-nav-workspaces" },
  { id: "billing",        label: "Billing",        icon: CreditCard,      testid: "admin-nav-billing" },
  { id: "amplify",        label: "Amplify",        icon: Share2,          testid: "admin-nav-amplify" },
  { id: "moderation",     label: "Moderation",     icon: ShieldAlert,     testid: "admin-nav-moderation" },
  { id: "integrations",   label: "Integrations",   icon: Plug,            testid: "admin-nav-integrations" },
  { id: "insights",       label: "Insights",       icon: BarChart3,       testid: "admin-nav-insights" },
  { id: "infrastructure", label: "Infrastructure", icon: Server,          testid: "admin-nav-infra" },
  { id: "settings",       label: "Settings",       icon: SettingsIcon,    testid: "admin-nav-settings" },
];

export default function AdminLayout({ active = "overview", onChange, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink">
      <div className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-line px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2"><img src={LOGO} alt="Ashra" className="h-7" /><span className="pill pill-warning">Admin</span></Link>
        <button onClick={() => setOpen(!open)} className="p-2 rounded-lg border border-line min-h-[44px] min-w-[44px] grid place-items-center" data-testid="admin-menu-toggle">
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-0 z-30 h-screen w-64 shrink-0 border-r border-line bg-white px-4 py-6 overflow-y-auto scrollbar-thin`} data-testid="admin-sidebar">
          <Link to="/admin" className="flex items-center gap-2 mb-6">
            <img src={LOGO} alt="Ashra" className="h-8" />
            <span className="pill pill-warning text-[10px]">Admin</span>
          </Link>

          <nav className="space-y-0.5" data-testid="admin-nav">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => { onChange?.(n.id); setOpen(false); }} data-testid={n.testid}
                className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                  active === n.id ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm" : "text-ink-soft hover:bg-zinc-50"
                }`}>
                <n.icon className="h-4 w-4 shrink-0" /> {n.label}
              </button>
            ))}
          </nav>

          <NavLink to="/dashboard" className="mt-6 flex items-center gap-2 text-xs text-meta hover:text-ai transition" data-testid="admin-back-creator">
            <ArrowLeft className="h-3 w-3" /> Back to Creator OS
          </NavLink>

          <div className="mt-6 rounded-2xl border border-line bg-zinc-50 p-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-white text-sm font-bold">{user?.name?.[0]?.toUpperCase() || "A"}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-ink">{user?.name}</p>
                <p className="text-[11px] text-meta truncate">Super admin</p>
              </div>
              <button onClick={doLogout} className="p-2 rounded-lg hover:bg-zinc-100 min-h-[40px] min-w-[40px] grid place-items-center" data-testid="admin-logout"><LogOut className="h-4 w-4" /></button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 min-h-screen">{children}</main>
      </div>
    </div>
  );
}
